<template>
  <SoSkeleton v-if="!selectableJobTypes.length" height="5em" width="100%" />
  <SoRadioGroup
    v-else
    v-model="jobType.selectedMeterTypeAes"
    :items="selectableJobTypes"
    :disabled="selectedMeterTypeAesDisabled"
    @update:model-value="updateField('selectedMeterTypeAes', $event)"
  />
  <SoSelect
    v-model="jobType.selectedInstallationType"
    :items="installationTypesAES"
    :disabled="selectedInstallationTypeDisabled"
    :rules="[rules.required]"
    label="Installation type (AES)"
    @update:model-value="updateField('selectedInstallationType', $event)"
  />
  <SoSelect
    v-model="jobType.selectedAppointmentType"
    label="Appointment type (AES)"
    :items="appointmentTypesAES"
    :disabled="selectedAppointmentTypeDisabled"
    :rules="[rules.required]"
    @update:model-value="updateField('selectedAppointmentType', $event)"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoRadioGroup from "@/components/formControls/SoRadioGroup.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import {
    meterTypesAES,
    installationTypesAES,
    appointmentTypesAES,
    jobTypes,
  } from "@/constants/imbp"
  import rules from "@/validators/rules"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import {
    checkBookingPermission,
    JobType,
  } from "@/flows/useCases/imbpBooking/imbpBookingFlowHelper"

  export default defineComponent({
    components: {
      SoRadioGroup,
      SoSelect,
      SoSkeleton,
    },
    props: {
      modelValue: {
        type: Object,
        default: () => ({}),
      },
      hasSmartMeter: {
        type: Boolean,
        default: null,
      },
    },
    emits: ["update:model-value"],
    data() {
      return {
        current: useCurrentAccountStore(),
        meterTypesAES,
        installationTypesAES,
        appointmentTypesAES,
        jobTypes,
        rules,
        selectedMeterTypeAesDisabled: false,
        selectedInstallationTypeDisabled: false,
        selectedAppointmentTypeDisabled: false,
        jobType: {
          selectedMeterTypeAes: "",
          selectedInstallationType: "",
          selectedAppointmentType: "",
          isAllowedToBook: true,
          errorMessage: null,
        } as JobType,
      }
    },
    computed: {
      selectableJobTypes() {
        const meterTypesToFilterOut: string[] = []
        const imbpAccount = this.current.imbpAccount.data

        if (!imbpAccount.mpan) {
          meterTypesToFilterOut.push(...["Electricity", "Dual"])
        }
        if (!imbpAccount.mprn) {
          meterTypesToFilterOut.push(...["Gas", "Dual"])
        }

        const filteredMeterTypes = this.meterTypesAES.filter(
          (meter) => !meterTypesToFilterOut.includes(meter.text),
        )

        return filteredMeterTypes
      },
      isSmets2Booking() {
        return (
          this.jobType.selectedAppointmentType &&
          this.jobType.selectedMeterTypeAes &&
          this.jobType.selectedInstallationType == "SMETS2" &&
          this.hasSmartMeter &&
          (!this.$route.query.job || this.$route.query.job === "smart-install")
        )
      },
    },
    created() {
      this.autoPopulateFields(this.$route.query)
    },
    methods: {
      autoPopulateFields(query) {
        if (!query.job) return

        const job = this.jobTypes.find((job) => job.name === query.job)

        if (!job?.params) return

        if (job.params.meter) {
          this.jobType.selectedMeterTypeAes = job.params.meter
          this.selectedMeterTypeAesDisabled = true
        }

        if (job.params.install) {
          this.jobType.selectedInstallationType = job.params.install
          this.selectedInstallationTypeDisabled = true
        }

        if (job.params.appointment) {
          this.jobType.selectedAppointmentType = job.params.appointment
          this.selectedAppointmentTypeDisabled = true
        }
      },
      updateField(fieldName: string, fieldValue: string) {
        this.jobType = {
          ...this.jobType,
          [fieldName]: fieldValue,
          isAllowedToBook: true,
          errorMessage: null,
        }

        if (this.isSmets2Booking) {
          this.bookingPermissionCheck()
        }

        this.$emit("update:model-value", this.jobType)
      },
      bookingPermissionCheck() {
        const { isAllowed, error } = checkBookingPermission(
          this.jobType.selectedAppointmentType,
        )

        this.jobType = {
          ...this.jobType,
          isAllowedToBook: isAllowed,
          errorMessage: error,
        }
      },
    },
  })
</script>
